<template>
    <q-select
        v-model="internalValue"
        :label="label"
        :options="filteredOptions"
        use-input
        fill-input
        hide-selected
        input-debounce="0"
        @filter="filterFn"
        @input-value="(val) => (internalValue = val)"
        outlined
        dense
        v-bind="$attrs"
        map-options
        emit-value
    >
        <template v-slot:prepend>
            <q-icon :name="internalValue || 'help'" />
        </template>

        <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
                <q-item-section avatar>
                    <q-icon :name="scope.opt" />
                </q-item-section>
                <q-item-section>
                    <q-item-label>{{ scope.opt }}</q-item-label>
                </q-item-section>
            </q-item>
        </template>

        <template v-slot:no-option>
            <q-item>
                <q-item-section class="text-grey">
                    No results. You can still type a custom icon name.
                </q-item-section>
            </q-item>
        </template>
    </q-select>
    <input type="hidden" :name="name" :value="internalValue" />
</template>

<script>
import { ref, watch } from "vue";

const iconList = [
    "place",
    "location_on",
    "person",
    "home",
    "work",
    "school",
    "hospital",
    "restaurant",
    "hotel",
    "local_parking",
    "directions_car",
    "directions_bus",
    "directions_railway",
    "directions_bike",
    "directions_walk",
    "info",
    "warning",
    "help",
    "star",
    "favorite",
    "flag",
    "camera_alt",
    "photo_camera",
    "event",
    "access_time",
    "shopping_cart",
    "local_library",
    "museum",
    "park",
    "terrain",
    "beach_access",
    "pool",
    "fitness_center",
    "church",
    "mosque",
    "synagogue",
    "temple_hindu",
    "temple_buddhist",
    "agriculture",
    "apartment",
    "architecture",
    "atm",
    "attach_money",
    "audiotrack",
    "auto_awesome",
    "backpack",
    "bakery_dining",
    "bed",
    "bike_scooter",
    "breakfast_dining",
    "brush",
    "bug_report",
    "build",
    "cake",
    "call",
    "campaign",
    "castle",
    "celebration",
    "child_care",
    "cleaning_services",
    "coffee",
    "computer",
    "construction",
    "contact_page",
    "coronavirus",
    "cottage",
    "dashboard",
    "description",
    "devices",
    "dns",
    "domain",
    "done",
    "edit",
    "electrical_services",
    "email",
    "emergency",
    "extension",
    "face",
    "factory",
    "family_restroom",
    "fastfood",
    "feedback",
    "female",
    "festival",
    "fingerprint",
    "fire_extinguisher",
    "fireplace",
    "flight",
    "forest",
    "forum",
    "g_translate",
    "games",
    "gavel",
    "gesture",
    "group",
    "groups",
    "handyman",
    "history",
    "hiking",
    "home_repair_service",
    "hot_tub",
    "icecream",
    "important_devices",
    "kayaking",
    "key",
    "kitchen",
    "landscape",
    "layers",
    "lightbulb",
    "local_activity",
    "local_airport",
    "local_bar",
    "local_cafe",
    "local_car_wash",
    "local_convenience_store",
    "local_dining",
    "local_drink",
    "local_fire_department",
    "local_florist",
    "local_gas_station",
    "local_grocery_store",
    "local_hospital",
    "local_hotel",
    "local_laundry_service",
    "local_mall",
    "local_movies",
    "local_offer",
    "local_pharmacy",
    "local_phone",
    "local_pizza",
    "local_post_office",
    "local_printshop",
    "local_see",
    "local_shipping",
    "local_taxi",
    "location_city",
    "luggage",
    "male",
    "map",
    "medical_services",
    "menu_book",
    "military_tech",
    "money",
    "movie",
    "nightlife",
    "notifications",
    "palette",
    "payments",
    "pets",
    "phone",
    "photo",
    "piano",
    "plumbing",
    "policy",
    "precision_manufacturing",
    "print",
    "public",
    "push_pin",
    "railway_alert",
    "ramen_dining",
    "receipt",
    "recycling",
    "rocket",
    "room",
    "route",
    "running_with_errors",
    "sailing",
    "sanitizer",
    "save",
    "schedule",
    "science",
    "scuba_diving",
    "search",
    "security",
    "self_improvement",
    "send",
    "sentiment_satisfied",
    "settings",
    "share",
    "shield",
    "shop",
    "shopping_bag",
    "shopping_basket",
    "skateboarding",
    "skipping_next",
    "slideshow",
    "snowboarding",
    "snowmobile",
    "spa",
    "sports",
    "sports_baseball",
    "sports_basketball",
    "sports_cricket",
    "sports_esports",
    "sports_football",
    "sports_golf",
    "sports_handball",
    "sports_hockey",
    "sports_kabaddi",
    "sports_mma",
    "sports_motorsports",
    "sports_rugby",
    "sports_soccer",
    "sports_tennis",
    "sports_volleyball",
    "stadium",
    "store",
    "storefront",
    "straighten",
    "style",
    "surfing",
    "support",
    "swap_calls",
    "tag",
    "tapas",
    "task",
    "taxi_alert",
    "theater_comedy",
    "thumb_up",
    "timer",
    "traffic",
    "train",
    "tram",
    "translate",
    "travel_explore",
    "trending_up",
    "vignette",
    "visibility",
    "volunteer_activism",
    "vpn_key",
    "wash",
    "watch",
    "water_damage",
    "waves",
    "wb_sunny",
    "wc",
    "weekend",
    "whatshot",
    "wifi",
    "wine_bar",
    "woman",
];

export default {
    name: "IconPickerWrapper",
    props: {
        modelValue: String,
        name: String,
        label: String,
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const internalValue = ref(props.modelValue || "");
        const filteredOptions = ref(iconList);

        watch(
            () => props.modelValue,
            (val) => {
                internalValue.value = val;
            },
        );

        watch(internalValue, (val) => {
            emit("update:modelValue", val);
        });

        const filterFn = (val, update) => {
            update(() => {
                if (val === "") {
                    filteredOptions.value = iconList;
                } else {
                    const needle = val.toLowerCase();
                    filteredOptions.value = iconList.filter(
                        (v) => v.toLowerCase().indexOf(needle) > -1,
                    );
                    // Also allow typing custom values
                    if (filteredOptions.value.indexOf(val) === -1) {
                        // We don't necessarily want to add it to the list, but q-select with use-input handles it
                    }
                }
            });
        };

        return {
            internalValue,
            filteredOptions,
            filterFn,
        };
    },
};
</script>
